import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { fetchContentDetail, toggleActivate, deleteContent, IContent } from 'api/koach/v2/content.api';
import { getCurrentLanguageCode } from 'helpers/String.helper';
import VideoPlayer from 'commons/video/VideoPlayer';

const KoachContentsContentsInfo = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [content, setContent] = useState<IContent | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);

  useEffect(() => {
    if (id) {
      fetchContentData(Number(id));
    }
  }, [id]);

  const fetchContentData = async (contentId: number) => {
    try {
      setIsLoading(true);
      setError(null);

      const response = await fetchContentDetail(contentId);
      setContent(response.content);
    } catch (err: any) {
      console.error('컨텐츠 상세 정보 조회 실패:', err);
      setError('컨텐츠 정보를 불러오는데 실패했습니다.');
    } finally {
      setIsLoading(false);
    }
  };

  const getTitle = (content: IContent): string => {
    // 기본 제목 사용, 없으면 번역에서 가져오기
    if (content.title) return content.title;
    if (content.multiTexts && content.multiTexts.length > 0) {
      const currentLang = getCurrentLanguageCode();
      const currentMultiText = content.multiTexts.find(mt => mt.languageCode === currentLang);
      return currentMultiText?.title || content.multiTexts[0]?.title || '제목 없음';
    }
    return '제목 없음';
  };


  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('ko-KR', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleStatusToggle = async () => {
    if (!content) return;

    try {
      setIsUpdatingStatus(true);
      const response = await toggleActivate(content.id, !content.isActivated);
      
      setContent({ ...content, isActivated: response.isActivated });
      alert(`컨텐츠가 ${response.isActivated ? '공개' : '비공개'}되었습니다.`);
    } catch (error: any) {
      console.error('컨텐츠 상태 업데이트 실패:', error);
      alert(error.message || '컨텐츠 상태 업데이트에 실패했습니다.');
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  const handleEdit = () => {
    navigate(`/koach/coaching/contents/${content?.id}/edit`);
  };

  const handleDelete = async () => {
    if (!content) return;

    const confirmDelete = window.confirm;
    if (!confirmDelete('정말 이 컨텐츠를 삭제하시겠습니까?')) {
      return;
    }

    try {
      await deleteContent(content.id);
      alert('컨텐츠가 삭제되었습니다.');
      navigate('/koach/coaching/contents');
    } catch (error: any) {
      console.error('컨텐츠 삭제 실패:', error);
      alert(error.message || '컨텐츠 삭제에 실패했습니다.');
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 pt-20 pb-4 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="flex justify-center items-center h-64">
            <div className="text-gray-500">로딩 중...</div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !content) {
    return (
      <div className="min-h-screen bg-gray-50 pt-20 pb-4 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="flex justify-center items-center h-64">
            <div className="text-red-500">{error || '컨텐츠를 찾을 수 없습니다.'}</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <main className="bg-gray-50 min-h-screen">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 pt-20">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center">
            <button
              onClick={() => navigate('/koach/coaching/contents')}
              className="mr-2 p-2 rounded-full hover:bg-gray-200"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <h2 className="text-3xl font-bold text-gray-900">
              {getTitle(content)}
            </h2>
          </div>
          <div className="flex gap-2">
            <button
              onClick={handleEdit}
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
            >
              수정
            </button>
            <button
              onClick={handleStatusToggle}
              disabled={isUpdatingStatus}
              className={`px-4 py-2 rounded-lg transition-colors ${
                content.isActivated
                  ? 'bg-green-500 text-white hover:bg-green-600'
                  : 'bg-gray-500 text-white hover:bg-gray-600'
              } disabled:opacity-50`}
            >
              {isUpdatingStatus ? '처리 중...' : content.isActivated ? '비공개로 변경' : '공개로 변경'}
            </button>
            <button
              onClick={handleDelete}
              className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
            >
              삭제
            </button>
          </div>
        </div>

        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-lg shadow-md overflow-hidden">
            {/* 비디오 */}
            {content.video && (
              <div className="w-full">
                <VideoPlayer
                  video={content.video}
                  className="w-full rounded-lg"
                  enableVideoLog={false}
                />
              </div>
            )}

            <div className="p-6">
              {/* 상태 배지 */}
              <div className="mb-4">
                <span className={`inline-block px-3 py-1 rounded-full text-sm ${
                  content.isActivated 
                    ? 'bg-green-100 text-green-800' 
                    : 'bg-gray-100 text-gray-800'
                }`}>
                  {content.isActivated ? '공개' : '비공개'}
                </span>
              </div>

              {/* 제목 */}
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                {getTitle(content)}
              </h3>

              {/* 기본 설명 */}
              {content.desc && (
                <div className="mb-6">
                  <p className="text-gray-700 whitespace-pre-wrap">
                    {content.desc}
                  </p>
                </div>
              )}

              {/* 번역된 설명 (다국어) */}
              {content.multiTexts && content.multiTexts.length > 0 && (
                <div className="mb-6 space-y-4">
                  {content.multiTexts.map((multiText, index) => {
                    const languageName = multiText.language?.names 
                      ? (multiText.language.names as { ko?: string; en?: string }).ko || multiText.languageCode 
                      : multiText.languageCode;
                    return (
                      <div key={index} className="border-l-4 border-[#CF3688] pl-4">
                        <div className="text-sm font-medium text-gray-500 mb-1">
                          {languageName}
                        </div>
                        {multiText.title && (
                          <h4 className="text-lg font-semibold text-gray-900 mb-2">
                            {multiText.title}
                          </h4>
                        )}
                        <p className="text-gray-700 whitespace-pre-wrap">
                          {multiText.desc}
                        </p>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* 메타 정보 */}
              <div className="border-t pt-4 mt-6">
                <div className="grid grid-cols-2 gap-4 text-sm text-gray-600">
                  <div>
                    <span className="font-medium">생성일:</span> {formatDate(content.createdAt)}
                  </div>
                  <div>
                    <span className="font-medium">수정일:</span> {formatDate(content.updatedAt)}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </main>
  );
};

export default KoachContentsContentsInfo;
